import {
  Button,
  HorizontalDivider,
  Icon,
  IconButton,
  NameAvatar,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import qs from 'qs';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SkeletonLoaders } from '@hero/components';
import { RECOGNITIONS_TAB } from '@hero/constants';
import { ROUTE_PATHS } from '@hero/routes';
import { useGetCSEntries } from '@hero/services/content-stack-service/hooks';
import { useGetGratitudes } from '@hero/services/order-service/hook';
import { convertHtmlMessageToString } from '@hero/utils';
import { formatTimeAgo } from '@hero/utils/dateUtils';

import * as S from './style';
import { filterDashboardOccasions, formatDateWithOrdinal } from './utils';

function ThankYouAndOccasions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  const { data: gratitudes, isLoading: isLoadingGratitudes } = useGetGratitudes({
    pagination: { limit: 3 },
    filters: { associationType: 'recognition' }
  });

  const { data: occasionsData, isLoading: isLoadingOccasions } = useGetCSEntries({
    entryCode: 'sgb_admin_dashboard_occasions'
  });

  const { allMonths, visibleMonths } = useMemo(() => {
    if (!occasionsData?.entries?.[0]?.occasions?.length)
      return { allMonths: {}, visibleMonths: {} };

    const occasions = filterDashboardOccasions(occasionsData?.entries?.[0]?.occasions);
    const today = dayjs();
    const currentYear = today.year();
    const nextYear = currentYear + 1;

    const preparedOccasions = occasions.map(occasion => {
      const parsedDate = dayjs(occasion.date);
      let adjustedDate = parsedDate.year(currentYear);

      if (adjustedDate.isBefore(today)) {
        adjustedDate = parsedDate.year(nextYear);
      }

      return {
        ...occasion,
        parsedDate: adjustedDate
      };
    });

    const sortedOccasions = preparedOccasions.sort(
      (a, b) => a.parsedDate.unix() - b.parsedDate.unix()
    );

    const monthOrder = [];
    const groupedByMonth = sortedOccasions.reduce((acc, occasion) => {
      const monthKey = occasion.parsedDate.format('MMMM');
      if (!acc[monthKey]) {
        acc[monthKey] = {
          monthDate: occasion.parsedDate,
          occasions: []
        };
        monthOrder.push(monthKey);
      }
      acc[monthKey].occasions.push({
        ...occasion,
        dayOfMonth: occasion.parsedDate.date()
      });

      acc[monthKey].occasions.sort((a, b) => a.dayOfMonth - b.dayOfMonth);

      return acc;
    }, {});

    const allMonths = monthOrder.reduce((acc, month) => {
      acc[month] = groupedByMonth[month];
      return acc;
    }, {});

    if (!showAll && monthOrder.length > 2) {
      const currentMonth = today.format('MMMM');
      const currentMonthIndex = monthOrder.indexOf(currentMonth);
      const visibleMonthKeys = monthOrder.slice(currentMonthIndex).slice(0, 2);

      const visibleMonths = visibleMonthKeys.reduce((acc, month) => {
        acc[month] = groupedByMonth[month];
        return acc;
      }, {});

      return { allMonths, visibleMonths };
    }

    return { allMonths, visibleMonths: allMonths };
  }, [showAll, occasionsData]);

  const showSeeMoreButton = useMemo(() => {
    if (!occasionsData?.entries?.[0]?.occasions?.length) return false;

    const allMonthKeys = Object.keys(allMonths);
    const visibleMonthKeys = Object.keys(visibleMonths);

    if (showAll) return true;

    const lastVisibleMonth = visibleMonthKeys[visibleMonthKeys.length - 1];
    const lastVisibleMonthIndex = allMonthKeys.indexOf(lastVisibleMonth);

    return lastVisibleMonthIndex < allMonthKeys.length - 1;
  }, [allMonths, visibleMonths, showAll, occasionsData]);

  const renderDate = occasion => {
    const date = dayjs(occasion.date);
    return (
      <S.DateBox>
        <Typography variant="caption1">{date.format('ddd')}</Typography>
        <Typography variant="subtitle2Bold">{date.format('D')}</Typography>
      </S.DateBox>
    );
  };

  if (isLoadingGratitudes || isLoadingOccasions) {
    return (
      <S.ThankYouAndOccasionsWrapper>
        <SkeletonLoaders.ThankYouAndOccasions />
      </S.ThankYouAndOccasionsWrapper>
    );
  }

  const allGratitudes = gratitudes?.pages?.flatMap(page => page.docs) || [];

  return (
    <S.ThankYouAndOccasionsWrapper>
      {allGratitudes.length > 0 && (
        <>
          <S.SectionHeader>
            <Typography variant="subtitle4Bold">{t('thankYouS')}</Typography>
            <IconButton
              color="neutral"
              variant="link"
              size="small"
              onClick={() => navigate(ROUTE_PATHS.GRATITUDE_BOARD)}
            >
              {t('seeAll')}
              <Icon name="right-chevron" />
            </IconButton>
          </S.SectionHeader>
          {allGratitudes.map(gratitude => (
            <S.GratitudeCard
              key={gratitude.id}
              onClick={() => {
                navigate(
                  `${ROUTE_PATHS.MY_RECOGNITION_DETAIL(gratitude.recognitionCode)}?initialNote=${gratitude._id}#${
                    RECOGNITIONS_TAB.tyNotes
                  }`
                );
              }}
            >
              <S.GratitudeCardHeader>
                <S.GratitudeCardUser>
                  <NameAvatar
                    name={`${gratitude.recipient.firstName} ${gratitude.recipient.lastName}`}
                    rounded
                    size={32}
                  />
                  <Typography variant="body2Bold">{gratitude.recipient.firstName}</Typography>
                </S.GratitudeCardUser>
                <Typography variant="caption1" emphasis="weak">
                  {formatTimeAgo(gratitude?.status?.acceptedAt)}
                </Typography>
              </S.GratitudeCardHeader>
              <Typography variant="body2" emphasis="weak">
                {`${convertHtmlMessageToString(gratitude?.experience?.thankYouMessage)}`.slice(
                  0,
                  100
                )}
                {`${gratitude?.experience?.thankYouMessage?.length > 100 ? '...' : ''}`}
              </Typography>
            </S.GratitudeCard>
          ))}
          <HorizontalDivider $margin="var(--size-24) 0" />
        </>
      )}
      <>
        <S.SectionHeader>
          <Typography variant="subtitle4Bold">{t('occasions')}</Typography>
          <IconButton
            color="neutral"
            variant="link"
            size="small"
            onClick={() => navigate(ROUTE_PATHS.GIFT_CATALOG)}
          >
            {t('browseGifts')}
            <Icon name="right-chevron" />
          </IconButton>
        </S.SectionHeader>
        <S.OccasionsWrapper>
          {Object.entries(visibleMonths).map(([month, data]) => (
            <S.MonthSection key={month}>
              <S.MonthTitle variant="body2" emphasis="weak">
                {month}
              </S.MonthTitle>
              <S.OccasionList>
                {data.occasions.map(occasion => (
                  <S.OccasionItem
                    key={`${occasion.name}-${occasion.date}`}
                    onClick={() => {
                      let queryString = {};

                      if (occasion?.entity_type?.toLowerCase() === 'collection') {
                        queryString = {
                          collectionIds: [occasion.collection_id]
                        };
                      } else {
                        queryString = {
                          categoryIds: [occasion.collection_id]
                        };
                      }

                      navigate(`${ROUTE_PATHS.GIFT_CATALOG}?${qs.stringify(queryString)}`);
                    }}
                  >
                    {renderDate(occasion)}
                    <S.OccasionInfo>
                      <VerticalDivider $height={32} />
                      <Typography variant="body2" style={{ minWidth: 120 }}>
                        <span style={{ marginRight: 4 }}>{occasion.emoji}</span>
                        {occasion.name}{' '}
                        {!!occasion.end_date &&
                          occasion.end_date !== occasion.date &&
                          `(${formatDateWithOrdinal(dayjs(occasion.date))} - ${formatDateWithOrdinal(
                            dayjs(occasion.end_date)
                          )})`}
                      </Typography>
                    </S.OccasionInfo>
                    <Icon name="right-chevron" size="lg" color="var(--color-fg-default)" />
                  </S.OccasionItem>
                ))}
              </S.OccasionList>
            </S.MonthSection>
          ))}
          {showSeeMoreButton && (
            <Button size="sm" variant="link" color="neutral" onClick={() => setShowAll(!showAll)}>
              {showAll ? t('seeLess') : t('seeMore')}
            </Button>
          )}
        </S.OccasionsWrapper>
      </>
    </S.ThankYouAndOccasionsWrapper>
  );
}

export { ThankYouAndOccasions };
